import { formatUnits } from '@ethersproject/units';
import { FC, useCallback } from 'react';
import useNotify from '../../../hooks/useNotify';
import useSiloRouter from '../../../hooks/useSiloRouter';
import {
  ActionForm,
  CustomFormCondition,
  DecideIsFullAmount,
  DoSubmit,
  LiquidityTooltip,
  SubmitButtonContent,
  useActionFormContext,
  ValidateForm,
} from '../common/ActionForm';
import {
  getCollateralAssets,
  getCollateralType,
  getEffectiveCollateral,
  getIsCollateralOnlyAsset,
  Recipe,
} from 'queries/decoratedSilo';
import { useThisDecoratedSilo } from '../silo';
import { useRouterSelectedAddress } from '../common/ActionForm/useRouterSelectedAddress';
import { useClearAssetFromQueryString } from '../silo/hooks';
import { DualCollateral } from './DualCollateral';
import { validator } from './validator';
import { getNewLTV } from './getNewLTV';
import { decideIsFullAmount as _decideIsFullAmount, FullAmountWarning } from './FullAmountWarning';
import { asDecimal, handleParsedError, parseUserInitiatedTransactionError } from '@silo/utils';
import { determineMax, GetMaxAmount, MaxAmount } from '../../../hooks/useMaxTokenAmount';
import { updateAsset } from './updateAsset';
import { BigNumber } from '@ethersproject/bignumber';
import { HealthFactorConfig } from '../common/HealthFactor';
import { BIG_ZERO } from 'app/constants';
import { GetNewUtilization } from '../../../hooks/useUtilizationImpact';
import { calculateUtilization } from 'app/functions/silo/calculations';
import posthog from 'posthog-js';
import { useAddTransaction } from '@silo/web3';
import { useFinishTransaction } from '../silo/useFinishTransaction';
import { useIsAssetActionFrozen } from '../common/ActionForm/useIsFrozen';
import { ProtocolInstance, createBigNumberWithDecimals } from '@silo/types';

export type DoWithdraw = DoSubmit;

export const useDoWithdraw = (): DoWithdraw => {
  const { siloAddress } = useThisDecoratedSilo();
  const { routerWithdraw, routerWithdrawConvexWrapper } = useSiloRouter();
  const addTransaction = useAddTransaction();
  const { notifyError, notifyWarning } = useNotify();
  const finishTransaction = useFinishTransaction();

  return useCallback(
    async ({ asset, amount, isFullAmount }) => {
      try {
        const { assetAddress, decimals, symbol } = asset;

        const withdraw = async () => {
          if (asset.isCurveLpAsset) {
            return await routerWithdrawConvexWrapper({
              siloAddress,
              assetAddress,
              amount: isFullAmount ? getEffectiveCollateral(asset) : amount,
              isFullAmount,
              isCollateralOnly: true,
            });
          }

          return await routerWithdraw({
            siloAddress,
            assetAddress,
            amount,
            isFullAmount,
            isCollateralOnly: getIsCollateralOnlyAsset(asset),
          });
        };

        const tx = await withdraw();

        const formattedAmount = asDecimal(formatUnits(amount, decimals));

        addTransaction(tx, `Withdraw ${formattedAmount} ${symbol}`);
        posthog.capture('withdraw', { siloAddress, assetAddress, amount, isFullAmount });

        const { status } = await tx.wait();

        if (status === 0) {
          throw new Error(`Transaction ${tx.hash} reverted`);
        }

        finishTransaction(getWithdrawRecipe({ asset, amount, isFullAmount }));
      } catch (err) {
        console.error('Error on withdraw:,', err);
        const parsed = parseUserInitiatedTransactionError(err);

        const captureAndNotify = (message: string) => {
          notifyError(message);
        };

        const { rethrow } = handleParsedError({
          onWarning: notifyWarning,
          onError: captureAndNotify,
          onUnparsed: captureAndNotify,
        })(parsed);

        // re-throw error to be handled by react-query
        if (rethrow) throw err;
      }
    },
    [
      routerWithdraw,
      routerWithdrawConvexWrapper,
      siloAddress,
      addTransaction,
      notifyError,
      notifyWarning,
      finishTransaction,
    ]
  );
};

type GetWithdrawRecipe = (...params: Parameters<DoWithdraw>) => Recipe;

const getWithdrawRecipe: GetWithdrawRecipe =
  ({ asset, amount, isFullAmount }) =>
  (draft) => {
    const isProtected = getIsCollateralOnlyAsset(asset);
    const { assetAddress } = asset;

    updateAsset({ draft, assetAddress, isFullAmount, isProtected, amount });
  };

const getMaxAmount: GetMaxAmount = ({ currentLTV, selectedAsset }) => {
  const getUserMax = (): MaxAmount => {
    if (currentLTV) {
      const { assetAddress, decimals } = selectedAsset;
      const collateralAssetMetrics = currentLTV.collateralAssetsMap[assetAddress];

      /**
       * TODO: figure out why this check is necessary. Without, there is an
       * error thrown for a full withdrawal with multiple collateral (need to be
       * borrowing too)
       *
       * It's some kind of race condition in the render cycle once the full
       * withdrawal finishes.
       */
      if (!collateralAssetMetrics) {
        return {
          type: 'protocolBalance',
          value: { decimals, bigNumber: BIG_ZERO },
        };
      }

      const {
        maxWithdrawTokens: { type, value },
      } = collateralAssetMetrics;

      return {
        type,
        value: {
          decimals,
          bigNumber: BigNumber.from(value),
        },
      };
    }

    return {
      type: 'protocolBalance',
      value: createBigNumberWithDecimals(getEffectiveCollateral(selectedAsset), selectedAsset.decimals),
    };
  };

  const userMax = getUserMax();

  if (getIsCollateralOnlyAsset(selectedAsset)) return userMax;

  const liquidity: MaxAmount = {
    type: 'liquidity',
    value: {
      bigNumber: selectedAsset.liquidity,
      decimals: selectedAsset.decimals,
    },
  };

  return determineMax([userMax, liquidity]);
};

const validateForm: ValidateForm = ({ normalizedTokenAmount, maxAmount, selectedAsset, isFullAmount }) => {
  const effectiveCollateral = getEffectiveCollateral(selectedAsset);

  return validator({
    selectedAsset,
    isFullAmount,
    normalizedAmount: normalizedTokenAmount,
    maxAmount: maxAmount.value.bigNumber,
    balance: effectiveCollateral,
    isProtected: getIsCollateralOnlyAsset(selectedAsset),
  });
};

const decideIsFullAmount: DecideIsFullAmount = ({ selectedAsset, amountInUSD, priceInUSD, normalizedTokenAmount }) =>
  _decideIsFullAmount({
    balance: getEffectiveCollateral(selectedAsset),
    amountInUSD,
    priceInUSD,
    selectedAsset,
    normalizedTokenAmount,
  });

const dualCollateralCondition: CustomFormCondition = ({ selectedAsset }) => {
  const collateralType = getCollateralType(selectedAsset);

  return collateralType === 'dual';
};

const healthFactorConfig: HealthFactorConfig = {
  getTokenAdjustment: ({ action, tokens }) => {
    if (action !== 'withdraw') return 0n;

    return tokens;
  },
  getMin: ({ riskFactor }) => riskFactor,
};

export interface WithdrawFormProps {
  protocolInstance: ProtocolInstance;
  doWithdraw: DoWithdraw;
}

export const WithdrawForm: FC<WithdrawFormProps> = ({ doWithdraw, protocolInstance }) => {
  const decoratedSilo = useThisDecoratedSilo();
  const routerSelectedAddress = useRouterSelectedAddress('withdraw');
  const clearAssetFromQueryString = useClearAssetFromQueryString('withdraw');
  const isActionFrozen = useIsAssetActionFrozen('withdraw');

  return (
    <ActionForm
      getMaxAmount={getMaxAmount}
      queryStringSelectedAddress={routerSelectedAddress}
      maxLabelSlot={
        <div className="flex gap-2 items-center">
          <span>Available to withdraw</span>
          <LiquidityTooltip action="withdraw" />
        </div>
      }
      submitButtonContentSlot={({ selectedAsset }) => (
        <SubmitButtonContent
          submitCopy={
            isActionFrozen(selectedAsset.assetAddress, decoratedSilo.siloAddress)
              ? `Withdrawing ${selectedAsset.symbol} is temporarily disabled`
              : `Withdraw`
          }
          submittingCopy="Withdrawing"
        />
      )}
      doSubmit={doWithdraw}
      getNewLTV={getNewLTV}
      getNewUtilization={getNewUtilization}
      assetsToDisplay={getCollateralAssets(decoratedSilo)}
      validateForm={validateForm}
      protocolInstance={protocolInstance}
      data-cy="withdraw-form"
      decideIsFullAmount={decideIsFullAmount}
      disableSubmit={({ selectedAsset }) => isActionFrozen(selectedAsset.assetAddress, decoratedSilo.siloAddress)}
      postAmountSlot={
        <>
          <FullAmountIndicator />
        </>
      }
      onSelectedAddressChange={clearAssetFromQueryString}
      customForm={{
        condition: dualCollateralCondition,
        slot: <DualCollateral />,
      }}
      healthFactorConfig={healthFactorConfig}
      termsOfUseAction="withdraw"
    />
  );
};

const getNewUtilization: GetNewUtilization = (amount, deposits, liquidity) => {
  return calculateUtilization(deposits - amount, deposits - liquidity);
};

const FullAmountIndicator: FC = () => {
  const { isFullAmount } = useActionFormContext();

  if (!isFullAmount) {
    return <></>;
  }

  return <FullAmountWarning />;
};
